import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TeamSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const teamMembers = [
    {
      name: 'Sarah Chen',
      role: 'CEO & Co-founder',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
      bio: 'Former Google AI researcher with 10+ years in machine learning',
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'CTO & Co-founder',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
      bio: 'Ex-Tesla AI engineer, expert in scalable ML infrastructure',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      name: 'Emily Watson',
      role: 'Head of AI Research',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
      bio: 'PhD in Deep Learning from Stanford, 50+ published papers',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      name: 'David Kim',
      role: 'Lead Engineer',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      bio: 'Full-stack expert with 12+ years in voice technology',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      name: 'Alex Chen',
      role: 'Product Manager',
      image: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400&h=400&fit=crop&crop=face',
      bio: 'Former Microsoft PM, specialist in AI product strategy',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  // Auto-slide every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % teamMembers.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [teamMembers.length]);

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Meet our <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">team</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Talented innovators building the future of AI
          </p>
        </motion.div>

        {/* Team Carousel */}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex justify-center">
            <div className="relative w-full max-w-4xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 300, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -300, scale: 0.8 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 30,
                    duration: 0.6
                  }}
                  className="flex justify-center"
                >
                  <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-800/50 rounded-3xl p-8 md:p-12 max-w-2xl w-full relative overflow-hidden group">
                    {/* Gradient Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${teamMembers[currentIndex].gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      {/* Profile Image */}
                      <div className="flex justify-center mb-8">
                        <div className="relative">
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-32 h-32 md:w-40 md:h-40 rounded-3xl overflow-hidden border-4 border-gray-700/50 group-hover:border-gray-600/50 transition-all duration-300"
                          >
                            <img
                              src={teamMembers[currentIndex].image}
                              alt={teamMembers[currentIndex].name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://ui-avatars.com/api/?name=${teamMembers[currentIndex].name}&size=400&background=1f2937&color=ffffff&format=png`;
                              }}
                            />
                          </motion.div>
                          {/* Floating decoration */}
                          <div className={`absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r ${teamMembers[currentIndex].gradient} rounded-full opacity-80 animate-pulse`}></div>
                        </div>
                      </div>

                      {/* Member Info */}
                      <div className="text-center">
                        <motion.h3 
                          className="text-2xl md:text-3xl font-bold text-white mb-2"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          {teamMembers[currentIndex].name}
                        </motion.h3>
                        
                        <motion.p 
                          className={`text-lg font-medium bg-gradient-to-r ${teamMembers[currentIndex].gradient} bg-clip-text text-transparent mb-4`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          {teamMembers[currentIndex].role}
                        </motion.p>
                        
                        <motion.p 
                          className="text-gray-300 text-base md:text-lg leading-relaxed max-w-lg mx-auto"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          {teamMembers[currentIndex].bio}
                        </motion.p>
                      </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse"></div>
                    <div className="absolute bottom-4 right-4 w-1.5 h-1.5 bg-purple-400/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center mt-8 space-x-3">
            {teamMembers.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative overflow-hidden rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-12 h-3 bg-gradient-to-r from-blue-500 to-cyan-500' 
                    : 'w-3 h-3 bg-gray-600 hover:bg-gray-500'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {index === currentIndex && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400"
                    initial={{ x: '-100%' }}
                    animate={{ x: '0%' }}
                    transition={{ duration: 4, ease: 'linear' }}
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* Team Member Navigation */}
          <div className="flex justify-center mt-6">
            <div className="flex space-x-2 bg-gray-900/30 rounded-2xl p-2 backdrop-blur-sm">
              {teamMembers.map((member, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {member.name.split(' ')[0]}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;